import React, { useState, useEffect } from 'react';
import { notifyError, notifySuccess } from '../Notification';
import './ModernInviteModal.css';

const ModernInviteModal = ({
  showInviteModal, 
  setShowInviteModal, 
  userBusinesses,
  selectedBusiness, // Add this prop back!
  onInviteUser
}) => {
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('User');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState('');
  const [userExistsError, setUserExistsError] = useState(null);

  // Use selectedBusiness prop instead of first business
  const currentBusiness = userBusinesses.find(business => 
    business.business_id === parseInt(selectedBusiness)
  ) || userBusinesses[0]; // Fallback to first business if selectedBusiness not found
  
  const businessName = currentBusiness?.business_name || 'Your Business';
  const businessInitial = businessName.charAt(0).toUpperCase();
  const businessId = selectedBusiness || userBusinesses[0]?.business_id || '';

  // Reset modal state when opening/closing
  useEffect(() => {
    if (showInviteModal) {
      setEmail('');
      setSelectedRole('User');
      setIsLoading(false);
      setShowSuccess(false);
      setInvitedEmail('');
      setUserExistsError(null);
    }
  }, [showInviteModal]);

  // Email validation
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isEmailValid = validateEmail(email.trim());

  // Role options configuration
  const roleOptions = [
    {
      id: 'User',
      name: 'User',
      icon: '👤',
      description: 'Can view and interact with content',
      className: 'user'
    },
    {
      id: 'Business_Admin',
      name: 'Admin', 
      icon: '⚡',
      description: 'Full access to manage everything',
      className: 'admin'
    }
  ];

  // Handle role selection
  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    // Clear user exists error when role changes
    if (userExistsError) {
      setUserExistsError(null);
    }
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // Clear user exists error when email changes
    if (userExistsError) {
      setUserExistsError(null);
    }
  };

  // Handle invitation sending - Use the selectedBusiness properly
  const handleSendInvitation = async () => {
    if (!isEmailValid || isLoading) return;

    if (!email || !selectedRole || !businessId) {
      notifyError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setUserExistsError(null);
    
    try {
      // Use selectedBusiness (the currently selected business in admin view)
      const formData = {
        email: email.trim(),
        role: selectedRole,
        businessId: businessId // This will be the selected business
      };

      await onInviteUser(formData);
      
      // Success - show success state
      setInvitedEmail(email.trim());
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Invitation error:', error);
      
      // Handle specific user already exists error
      if (error.response?.data?.code === 'USER_ALREADY_EXISTS') {
        setUserExistsError(error.response.data);
      } else {
        // Handle other errors through notification
        const errorMessage = error.response?.data?.message || error.message || 'Failed to send invitation';
        notifyError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle update role
  const handleUpdateRole = () => {
    notifyError('Role update functionality coming soon!');
  };

  // Handle view access control
  const handleViewAccessControl = () => {
    setShowInviteModal(false);
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      setShowInviteModal(false);
      setUserExistsError(null);
      setEmail('');
      setSelectedRole('User');
      setShowSuccess(false);
      setInvitedEmail('');
    }
  };

  // Handle done after success
  const handleDone = () => {
    setShowInviteModal(false);
    // Reset state
    setEmail('');
    setSelectedRole('User');
    setShowSuccess(false);
    setInvitedEmail('');
    setUserExistsError(null);
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isEmailValid && !isLoading && !userExistsError) {
      handleSendInvitation();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!showInviteModal) return null;

  return (
    <div className="modern-invite-overlay" onClick={handleClose}>
      <div className="modern-invite-modal" onClick={(e) => e.stopPropagation()}>
        {!showSuccess ? (
          // Invitation Form
          <>
            <div className="modern-modal-header">
              <button className="modern-close-btn" onClick={handleClose} disabled={isLoading}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
              
              <div className="modern-invite-icon">
                <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                </svg>
              </div>
              
              <h2 className="modern-modal-title">Invite Team Member</h2>
              <p className="modern-modal-subtitle">Add someone new to collaborate with your team</p>
            </div>

            <div className="modern-modal-body">
              {/* User Already Exists Error Card */}
              {userExistsError && (
                <div className="modern-error-card">
                  <div className="modern-error-header">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <h3>User Already Has Access</h3>
                  </div>
                  
                  <div className="modern-error-details">
                    <p><strong>{userExistsError.data?.name || userExistsError.data?.email}</strong> already has access to this business.</p>
                    <p>Current role: <span className="modern-role-badge">{userExistsError.data?.currentRole}</span></p>
                  </div>
                  
                  <div className="modern-error-actions">
                    <button className="modern-btn modern-btn-secondary" onClick={handleUpdateRole}>
                      Update Role Instead
                    </button>
                    <button className="modern-btn modern-btn-outline" onClick={handleViewAccessControl}>
                      View Access Control
                    </button>
                  </div>
                </div>
              )}

              <div className="modern-business-context">
                <div className="modern-business-icon">{businessInitial}</div>
                <div className="modern-business-info">
                  <div className="modern-business-name">{businessName}</div>
                  <div className="modern-business-context-text">They'll be invited to join this business</div>
                </div>
              </div>

              <div className="modern-input-group">
                <label className="modern-input-label">Email Address</label>
                <div className="modern-email-input-container">
                  <input 
                    type="email" 
                    className={`modern-email-input ${userExistsError ? 'input-error' : ''}`}
                    placeholder="colleague@company.com"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
              </div>

              <div className="modern-role-selector">
                <label className="modern-input-label">Choose their role</label>
                <div className="modern-role-grid">
                  {roleOptions.map((role) => (
                    <div 
                      key={role.id}
                      className={`modern-role-option ${role.className} ${selectedRole === role.id ? 'selected' : ''}`}
                      onClick={() => !isLoading && handleRoleSelect(role.id)}
                    >
                      <div className="modern-role-icon">{role.icon}</div>
                      <div className="modern-role-name">{role.name}</div>
                      <div className="modern-role-description">{role.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modern-modal-actions">
              <button 
                className="btn btn-secondary-outline" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}
                onClick={handleSendInvitation}
                disabled={!isEmailValid || isLoading || !businessId}
              >
                <span className="modern-btn-text">
                  {isLoading ? (
                    <>
                      <div className="modern-spinner"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                      </svg>
                      Send Invitation
                    </>
                  )}
                </span>
                {isLoading && <div className="modern-loading-spinner"></div>}
              </button>
            </div>
          </>
        ) : (
          // Success State
          <div className="modern-success-state">
            <div className="modern-success-icon">✓</div>
            <h2 className="modern-success-title">Invitation Sent!</h2>
            <p className="modern-success-message">
              We've sent an invitation to <strong>{invitedEmail}</strong> to join <strong>{businessName}</strong>. 
              They'll receive an email with instructions to join your team.
            </p>
            <button className="modern-btn modern-btn-primary" onClick={handleDone}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernInviteModal;